import { z } from "zod"

import { ApiAccountMonthCellSchema } from "./missing-statements.js"

/* The accounts surface (PRD: engagement-analysis-layer, phase 1).
   These are the single definition of the request and response shapes: the API
   routes in apps/api/src/routes/accounts import them directly, and the
   api-client parses responses through them, so the two can never disagree. */

/** Mirrors the accounts.account_type check constraint in packages/db. */
export const ApiAccountTypeSchema = z.enum([
  "checking",
  "savings",
  "credit_card",
  "brokerage",
  "unknown",
])

export type ApiAccountType = z.infer<typeof ApiAccountTypeSchema>

export const accountDisplayNameMaxLength = 120

/** One account as the Accounts screen lists it: identity plus period coverage. */
export const ApiAccountResponseSchema = z.object({
  id: z.string().uuid(),
  institutionName: z.string(),
  institutionKey: z.string().nullable(),
  accountName: z.string().nullable(),
  accountNumberLast4: z.string().nullable(),
  accountType: ApiAccountTypeSchema,
  currency: z.string().nullable(),
  /** User override; null means the client derives a label. */
  displayName: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  periodCount: z.number().int().nonnegative(),
  /** Earliest period_start across the account's periods; null with no periods. */
  firstPeriodStart: z.string().nullable(),
  /** Latest period_end across the account's periods; null with no periods. */
  lastPeriodEnd: z.string().nullable(),
  /** The last 12 closed months, oldest first: which statements you have. */
  recentMonths: z.array(ApiAccountMonthCellSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
})

export type ApiAccountResponse = z.infer<typeof ApiAccountResponseSchema>

/** Archived accounts are included; the client decides whether to show them. */
export const ApiAccountListResponseSchema = z.object({
  accounts: z.array(ApiAccountResponseSchema),
})

export type ApiAccountListResponse = z.infer<typeof ApiAccountListResponseSchema>

export const ApiUpdateAccountBodySchema = z.object({
  /** Null clears the override so the derived label shows again. */
  displayName: z
    .string()
    .trim()
    .min(1)
    .max(accountDisplayNameMaxLength)
    .nullable()
    .optional(),
  /** True archives the account, false restores it. */
  archived: z.boolean().optional(),
})

export type ApiUpdateAccountBody = z.infer<typeof ApiUpdateAccountBodySchema>

export const ApiMergeAccountsBodySchema = z.object({
  /** The account that survives; the account in the path is archived into it. */
  targetAccountId: z.string().uuid(),
})

export type ApiMergeAccountsBody = z.infer<typeof ApiMergeAccountsBodySchema>

export const ApiMergeAccountsResponseSchema = z.object({
  /** The target, refreshed after the merge. */
  account: ApiAccountResponseSchema,
  periodsMoved: z.number().int().nonnegative(),
})

export type ApiMergeAccountsResponse = z.infer<
  typeof ApiMergeAccountsResponseSchema
>
